import FormError from '@/components/common/FormError';
import StandaloneForm from '@/components/common/StandaloneForm';
import FormPayment from '@/components/payment/FormPayment';
import NextButton from '@/components/NextButton';
import PrevButton from '@/components/buttons/PrevButton';
import RegistrationNavigation from '@/components/registration/RegistrationNavigation';
import StepLabelCheck from '@/components/StepLabelCheck';
import StepUpdater from '@/components/StepUpdater';
import { ASMHomePage, EARLY_BIRD_FORM_DEFINITION_ID } from '@/constants/constants';
import { AdminContext } from '@/contexts/AdminContext';
import AppSettingsContext from '@/contexts/AppSettingsContext';
import { FieldActionContext } from '@/contexts/FieldActionContext';
import { FORM_DEBUG } from '@/formSettings';
import getCreditCardErrorMessage from '@/utils/getCreditCardErrorMessage';

import * as Dtos from '@/api/dtos';
import { FieldContext } from '@/contexts/FieldContext';
import useEarlyBirdByRegistrationId from '@/hooks/useEarlyBirdByRegistrationId';
import useEarlyBirdPrice from '@/hooks/useEarlyBirdPrice';
import useSpeakerByGuid from '@/hooks/useSpeakerByGuid';
import {
    Box,
    Button,
    CircularProgress,
    Grid,
    Paper,
    Step,
    StepContent,
    Stepper,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    FormSubmitButton,
    FormsFeatureContext,
    IValidationError,
    useFormLookups,
    useFormMetadata,
    useFormValidate
} from '@ngt/forms';
import {
    IFormSubmit,
    ValidateOn,
} from '@ngt/forms-core';
import { ResponseStatus } from '@servicestack/client/dist';
import { DateTime } from 'luxon';
import React, { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print/lib';
import EarlyBirdInvoice from './EarlyBirdInvoice';
import EarlyBirdRegistered from './steps/EarlyBirdRegistered';
import EarlyBirdRegistrationSteps from './steps/EarlyBirdRegistrationSteps';

const EarlyBirdForm: React.FunctionComponent = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down(650));
    const formMetadata = useFormMetadata(EARLY_BIRD_FORM_DEFINITION_ID);
    const { data: lookups } = useFormLookups(formMetadata, {
        revalidateOnFocus: false,
        refreshWhenOffline: false,
    });
    const validate: any = useFormValidate(formMetadata);
    const { serviceStackClient } = useContext(FormsFeatureContext);
    const { data: appSettings, loading: appSettingsLoading } = useContext(AppSettingsContext);

    const [error, setError] = React.useState<any>();
    const [hasError, setHasError] = React.useState<boolean>(false);
    const [earlyBirdFormFromSave, setEarlyBirdFormFromSave] = React.useState<Dtos.EarlyBirdForm>();
    const [activeStep, setActiveStep] = React.useState(0);
    const [completed, setCompleted] = React.useState<{ [k: number]: boolean }>({});
    const [openPaymentModal, setOpenPaymentModal] = React.useState(false);
    const [, setFormBeforePayment] = React.useState<Dtos.Registration>();
    const { price: earlyBirdPrice, priceLoaded } = useEarlyBirdPrice();

    const totalSteps = useCallback(() => {
        return EarlyBirdRegistrationSteps.length;
    }, []);

    const completedSteps = useCallback(() => {
        const keys = Object.keys(completed);

        for (let i = 0; i < keys.length; i++) {
            if (!completed[+keys[i]]) {
                return i;
            }
        }

        return keys.length;
    }, [completed]);

    const stepLabelOnClick = useCallback(
        (index: number) => {
            const finishedSteps = completedSteps();

            if (finishedSteps >= index) {
                setActiveStep(index);
            } 
            else {
                setActiveStep(finishedSteps);
            }
        },
        [completedSteps, setActiveStep]
    );

    const isLastStep = useCallback(() => {
        return activeStep === totalSteps() - 1;
    }, [totalSteps, activeStep]);

    const allStepsCompleted = useCallback(() => {
        return completedSteps() === totalSteps();
    }, [completedSteps, totalSteps]);

    const handleNext = useCallback(() => {
        // const {fields} = useFormState({fields: true});
        const lastStep = isLastStep();
        const allStepsComplete = allStepsCompleted();

        const newActiveStep =
            lastStep && !allStepsComplete // It's the last step, but not all steps have been completed,
                ? // find the first step that has been completed
                    EarlyBirdRegistrationSteps.findIndex((_step, i) => !(i in completed))
                : activeStep + 1;

        const finishedSteps = completedSteps();

        if (finishedSteps >= newActiveStep) {
            setActiveStep(newActiveStep);
        } else {
            setActiveStep(finishedSteps);
        }
    }, [isLastStep, allStepsCompleted, completedSteps, completed, activeStep, setActiveStep]);

    const handleBack = () => {
        setActiveStep((c) => c - 1);
    };

    const handleSnackClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }

        setHasError(false);
    };

    const paymentStateRef = useRef<{
        paymentComplete: (clientReferenceId?: string) => void;
        paymentFailure: (error: any) => void;
    }>();

    const awaitPayment = useCallback(() => {
        return new Promise<string | undefined>((resolve, reject) => {
            paymentStateRef.current = {
                paymentComplete: (clientReferenceId) => {
                    resolve(clientReferenceId);
                    paymentStateRef.current = undefined;
                },
                paymentFailure: (error) => {
                    reject(error);
                    paymentStateRef.current = undefined;
                },
            };
        });
    }, [paymentStateRef]);

    const handleStripePaymentComplete = useCallback(
        (clientReferenceId?: string) => {
            setOpenPaymentModal(false);

            if (paymentStateRef.current) {
                if (clientReferenceId) {
                    paymentStateRef.current.paymentComplete(clientReferenceId);
                } 
                else {
                    paymentStateRef.current.paymentFailure('clientReferenceId not found!');
                }
            }

            // verify it somehow

            // if it is successful then save the form and add the payment into the database
        },
        [setOpenPaymentModal, paymentStateRef]
    );

    const onSubmit: IFormSubmit<Dtos.EarlyBirdForm, IValidationError> = async (
        formState,
        formActions
    ) => {
        if (!formState.value) return;

        const request = new Dtos.PostEarlyBirdRegistrationSave({
            earlyBirdForm: {
                ...formState.value,
                payments: [...(formState.value.payments ?? [])],
            },
        });
        
        if (
            formState.value?.payments?.[0]?.paymentType === (Dtos.AdminPaymentType.Card as number)
        ) {
            setFormBeforePayment(formState.value);
            setOpenPaymentModal(true);

            try {
                const clientReferenceId = await awaitPayment();

                if (!clientReferenceId) {
                    // cancelled.
                    return;
                }

                request.earlyBirdForm!.payments = [
                    new Dtos.Payment({
                        amountPaid: formState.value.cost,
                        receiptNumber: clientReferenceId,
                        paymentType: Dtos.AdminPaymentType.Card,
                        attemptNumber: 1,
                        paymentDate: DateTime.now().toISO({
                            includeOffset: false,
                        }),
                        paymentSuccess: true,
                    }),
                ];
            } 
            catch (error) {
                // do something cause stuff done broke.
                return;
            }
        }
        try {
            const response = await serviceStackClient.post(request);
            if (response.payment?.paymentSuccess !== false) {
                if (response.registration) {
                    setEarlyBirdFormFromSave(response.registration);
                } 
                else {
                    console.error('Registration object missing in response!');
                    setHasError(true);
                    setError({
                        responseStatus: {
                            message:
                                'Registration successful, but server did not return registration details.',
                        } as ResponseStatus,
                    });
                }
            } 
            else {
                formActions.setValue(response.registration);
                setHasError(true);
                setError({
                    responseStatus: {
                        message: (
                            <>
                                {response.payment.paymentStatus
                                    ? getCreditCardErrorMessage(response.payment.paymentStatus)
                                    : 'An error occured while trying to save the form'}
                                {response.payment.paymentMessage && (
                                    <Box sx={{ pt: 2 }}>
                                        <strong>Bank Message:</strong>{' '}
                                        {response.payment.paymentMessage}
                                    </Box>
                                )}
                            </>
                        ) as any,
                    } as ResponseStatus,
                });
            }
        } 
        catch (err: any) {
            setHasError(true);
            setError(err);
        }
    };

    const invoiceRef = useRef<React.ReactInstance>();
    const params = useParams();
    const registrationGuid = params?.guid;

    const earlyBirdRegistrationEndDate = appSettings?.earlyBirdRegistrationEndDate
        ? DateTime.fromISO(appSettings.earlyBirdRegistrationEndDate)
        : undefined;
    const isEarlyBirdRegistrationClosed = earlyBirdRegistrationEndDate?.isValid
        ? DateTime.now() > earlyBirdRegistrationEndDate.endOf('day')
        : true;

    const { registration, loading: speakerLoading } = useSpeakerByGuid(registrationGuid, {
        revalidateOnFocus: false,
    });

    const initialFormValue = useMemo(() => new Dtos.EarlyBirdForm({
        ...(registration as unknown as Partial<Dtos.EarlyBirdForm>),
        id: undefined,
        registrationId: registration?.id,
        payments: undefined,
        cost: earlyBirdPrice,
    }), [earlyBirdPrice, registration]);

    const { earlyBirdRegistration, isLoading } = useEarlyBirdByRegistrationId(registrationGuid);

    const hasCompletedEarlyBirdRegistration = !!earlyBirdRegistration;

    const handlePrint = useReactToPrint({
        content: () => invoiceRef.current ?? null,
    });

    return (
        <>
            {isLoading || appSettingsLoading || !priceLoaded || (!!registrationGuid && speakerLoading) ? (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '400px',
                    }}>
                    <CircularProgress />
                </Box>
            ) : hasCompletedEarlyBirdRegistration ? (
                <EarlyBirdRegistered />
            ) : isEarlyBirdRegistrationClosed ? (
                <Box className="box">
                    <Paper
                        sx={{
                            p: { xs: 2, sm: 3 },
                            my: 2,
                            textAlign: 'center',
                            mb: 5,
                            mt: 9,
                        }}>
                        <Typography variant="h2">Early Bird Registration Closed</Typography>
                        <Typography variant="body1" sx={{ mt: 2 }}>
                            Early registration for next year closed on{' '}
                            <strong>{earlyBirdRegistrationEndDate?.toLocaleString()}</strong>.
                        </Typography>
                        <Button
                            variant="contained"
                            color="secondary"
                            size="large"
                            sx={{
                                mt: 3,
                                minWidth: '150px',
                                border: 'solid',
                                borderColor: 'secondary.main',
                                borderRadius: '20px',
                                '&:hover': {
                                    backgroundColor: 'primary.main',
                                    borderColor: 'primary.main',
                                },
                            }}
                            href={ASMHomePage}>
                            <Typography variant="body2">Return Home</Typography>
                        </Button>
                    </Paper>
                </Box>
            ) : (
                <StandaloneForm
                    initialValue={initialFormValue}
                    debug={FORM_DEBUG}
                    lookups={lookups}
                    onValidate={validate}
                    validateOn={ValidateOn.onChange}
                    validateOnReset={true}
                    onSubmit={onSubmit}>
                    <AdminContext.Provider value={false}>
                        <StepUpdater setCompleted={setCompleted} />
                        <RegistrationNavigation />

                        <Box className="box">
                            {earlyBirdFormFromSave ? (
                                <Box
                                    sx={{
                                        pb: '50px',
                                    }}>
                                    <Grid
                                        container
                                        justifyContent="space-between"
                                        spacing={2}
                                        sx={{
                                            px: {
                                                xs: '10%',
                                                sm: '9%',
                                                md: '20%',
                                                lg: '28%',
                                            },
                                            mt: {
                                                xs: '50px',
                                                sm: '50px',
                                            },
                                        }}>
                                        <Grid item xs={12}>
                                            <Typography variant="h2">Success!</Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography ml={2}>
                                                A copy of the invoice has been emailed to the
                                                address provided
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <Button
                                                variant="contained"
                                                color="secondary"
                                                size="large"
                                                sx={{
                                                    width: {
                                                        xs: '100%',
                                                        md: '100%',
                                                    },
                                                    minWidth: '160px',
                                                    border: 'solid',
                                                    borderColor: 'secondary.main',
                                                    borderRadius: '20px',
                                                    '&:hover': {
                                                        backgroundColor: 'primary.main',
                                                        borderColor: 'primary.main',
                                                    },
                                                }}
                                                onClick={() => handlePrint()}>
                                                <Typography variant="body2">
                                                    Print Invoice
                                                </Typography>
                                            </Button>
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <Button
                                                variant="contained"
                                                color="secondary"
                                                size="large"
                                                sx={{
                                                    width: '100%',
                                                    minWidth: '150px',
                                                    border: 'solid',
                                                    borderColor: 'secondary.main',
                                                    borderRadius: '20px',
                                                    '&:hover': {
                                                        backgroundColor: 'primary.main',
                                                        borderColor: 'primary.main',
                                                    },
                                                }}
                                                href={ASMHomePage}>
                                                <Typography variant="body2">Return Home</Typography>
                                            </Button>
                                        </Grid>
                                    </Grid>
                                    {!isMobile && (
                                        <EarlyBirdInvoice
                                            ref={invoiceRef}
                                            key={'invoice' + earlyBirdFormFromSave.registrationId}
                                            earlyBirdForm={earlyBirdFormFromSave}
                                        />
                                    )}
                                </Box>
                            ) : (
                                <>
                                    <Paper
                                        sx={{
                                            p: { xs: 2, sm: 3 },
                                            my: 2,
                                            textAlign: 'center',
                                            mb: 5,
                                            mt: 9,
                                        }}>
                                        <Typography
                                            variant="h1"
                                            sx={{
                                                fontSize: {
                                                    xs: '2rem',
                                                    sm: '2.5rem',
                                                    md: '3rem',
                                                },
                                                letterSpacing: 0.5,
                                            }}>
                                            ASM 2027
                                        </Typography>
                                        <Typography
                                            variant="h2"
                                            sx={{
                                                fontSize: {
                                                    xs: '1.2rem',
                                                    sm: '1.5rem',
                                                    md: '1.8rem',
                                                },
                                                mt: 1,
                                            }}>
                                            Early Bird Registration
                                        </Typography>
                                        <Typography variant="body1" sx={{ mt: 2 }}>
                                            Sign up before &nbsp;
                                            <strong>{earlyBirdRegistrationEndDate?.toLocaleString()}</strong> to
                                            secure the early bird rate!
                                        </Typography>
                                    </Paper>
                                    <Stepper
                                        nonLinear
                                        sx={{
                                            marginTop: '20px',
                                            marginBottom: '20px',
                                            ml: { xs: 1, md: 0 },
                                            '& .MuiStepContent-root': { pl: 1 },
                                        }}
                                        activeStep={activeStep}
                                        orientation="vertical">
                                        {EarlyBirdRegistrationSteps.map(
                                            ({ label, Inputs }, index) => (
                                                <Step key={label} completed={completed[index]}>
                                                    <StepLabelCheck
                                                        onClick={() => stepLabelOnClick(index)}
                                                        label={label}
                                                        index={activeStep}
                                                        setComplete={setCompleted}
                                                    />
                                                    <StepContent>
                                                        <Inputs />
                                                        {
                                                            activeStep === 0 ? (
                                                                <NextButton
                                                                    onClick={handleNext}
                                                                    index={activeStep}
                                                                    setComplete={setCompleted}
                                                                />
                                                            ) : activeStep === 1 ? (
                                                                <Box>
                                                                    <FormSubmitButton
                                                                        variant="contained"
                                                                        sx={{
                                                                            float: 'right',
                                                                            marginTop: '20px',
                                                                        }}>
                                                                        Submit
                                                                    </FormSubmitButton>
                                                                    <PrevButton onClick={handleBack} />
                                                                    <FormError
                                                                        error={error}
                                                                        hasError={hasError}
                                                                        handleClose={handleSnackClose}
                                                                        index={0}
                                                                    />
                                                                </Box>
                                                            ) : (
                                                                <Box>
                                                                    <NextButton
                                                                        onClick={handleNext}
                                                                        index={activeStep}
                                                                        setComplete={setCompleted}
                                                                    />
                                                                    <PrevButton onClick={handleBack} />
                                                                </Box>
                                                            )
                                                        }
                                                    </StepContent>
                                                </Step>
                                            )
                                        )}
                                    </Stepper>
                                </>
                            )}
                        </Box>
                    </AdminContext.Provider>
                    {
                        openPaymentModal && (
                            <FormPayment
                                handleStripePaymentComplete={handleStripePaymentComplete}
                                handlePaymentCancel={() => {
                                    setOpenPaymentModal(false);

                                    paymentStateRef.current?.paymentComplete(undefined);
                                }}
                            />
                        )
                    }
                </StandaloneForm>
            )}
        </>
    );
};

const WrappedForm: React.FunctionComponent = () => {
    const [fields, setFields] = useState<Record<number, string[]>>({});
    return (
        <FieldContext.Provider value={fields}>
            <FieldActionContext.Provider value={setFields}>
                <EarlyBirdForm />
            </FieldActionContext.Provider>
        </FieldContext.Provider>
    );
};

export default WrappedForm;
